// AccountOrderDetail — the order detail INSIDE the account shell (redesign
// Task 5). Owns the GET /me/orders/{id} fetch the retired MarketplaceEmbed
// order-detail route used to own (the route now mounts AccountCenter with
// initialOrderId, which lands here), plus loading / error / Retry / not-found
// states, and renders OrderDetailView below the shell's page header.
//
// The "Back to orders" control is OrderDetailView's own top link on success —
// this component only hands it onBack there, so the composed view exposes
// exactly one. ⚠️ AMENDED 2026-10-03 (review P2): the failed and not-found
// branches render their own "← Back to orders" link too — without one, a
// failed or missing order wedged the Orders section (viewingOrderId had no
// clear path out of those states; Retry cannot succeed for a deleted or
// unauthorized order).
//
// F5: the addresses come from the order envelope's frozen snapshots — this
// component never touches listAddresses() (it does not import address-api).
//
// Inline styles only — no Tailwind. Themed via useEmbedTokens().

import { useEffect, useState } from "react";
import { useEmbedTokens, EmbedText, EmbedButton, EmbedLink } from "../../embed-shared/primitives";
import { embedApiFetch } from "../../embed-shared/api-client";
import type { OrderDetail } from "../../embed-shared/types";
import { OrderDetailView } from "./OrderDetailView";

export interface AccountOrderDetailProps {
  orderId: string;
  onBack: () => void;
}

export function AccountOrderDetail({ orderId, onBack }: AccountOrderDetailProps) {
  const t = useEmbedTokens();
  const [detail, setDetail] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  // Bumped by Retry to re-run the effect — one fetch path, no duplicated
  // load function in a dependency array.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    embedApiFetch<OrderDetail>(`/api/v1/marketplace/me/orders/${orderId}`)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch(() => {
        // The server message is never shown here — a failed order fetch is
        // one sentence and a retry, not an error transcript.
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, attempt]);

  if (loading) {
    return (
      <p role="status" style={{ margin: 0 }}>
        <EmbedText tone="secondary">Loading order...</EmbedText>
      </p>
    );
  }

  if (failed) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.md, alignItems: "flex-start" }}>
        {/* The escape hatch (review P2): without it a transient failure wedges
            the section — Retry cannot succeed for a deleted/unauthorized order. */}
        <EmbedLink onClick={onBack}>← Back to orders</EmbedLink>
        <div role="alert">
          <EmbedText tone="error">Failed to load the order.</EmbedText>
        </div>
        <EmbedButton type="button" variant="secondary" onClick={() => setAttempt((a) => a + 1)}>
          Retry
        </EmbedButton>
      </div>
    );
  }

  // A 2xx that unwraps to something without an order payload (the
  // client yields null/undefined when the envelope carries no data) — the not-found
  // state, not a crash on detail.order.order_number. Retry is pointless here,
  // so the back link is the only action (review P2).
  if (!detail || !detail.order) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.md, alignItems: "flex-start" }}>
        <EmbedLink onClick={onBack}>← Back to orders</EmbedLink>
        <div role="alert">
          <EmbedText tone="error">This order could not be loaded.</EmbedText>
        </div>
      </div>
    );
  }

  return <OrderDetailView detail={detail} onBack={onBack} />;
}
